import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Divider } from '@/components/ui/daisy';
import { useQuestionFormsOrchestrator } from '@/data/context/QuestionFormsOrchestrator';
import { useFormRegistration } from '@/data/hook/question/useFormRegistration';
import { useQuestionExamGradeCtx } from '@/data/context/QuestionExamGradeContext';
import { PointsSection } from '../fields/PointsSection';

import { FeedbackCreateDTO } from '@/models/Feedback';
import { QuizQuestion } from '@/models/Question';
import {
  DEFAULT_LANGUAGE,
  SupportedLanguage,
  TranslatableDict,
} from '@/models/TranslatableField';
import { normalizeTranslatableField } from '@/utils/translatable-field/normalizeTranslatableField';
import QuizAnswersForm from './QuizAnswersForm';
import { QuestionContextField } from '../fields/QuestionContextField';
import { QuestionTitleField } from '../fields/QuestionTitleField';
import { QuestionFormContainer } from '../QuestionFormContainer';
import QuestionPreview from '@/components/question/forms/QuestionPreview';
import { QuizAnswerCreateDTO } from '@/models/Answer';
import { adapterQuizAnswers } from '@/utils/questions/adapterAnswersDTO';
import { adapterFeedbacks } from '@/utils/questions/adapterFeedbacks';
import { useQuizPreviewQuestion } from '@/data/hook/question/useQuizPreviewQuestion';

export type QuizFormFields = {
  title: TranslatableDict;
  context: TranslatableDict;
  quizAnswers: QuizAnswerCreateDTO[];
  feedbacks: FeedbackCreateDTO[];
  maxGrade?: number;
};

type QuizFormProps = {
  isActive?: boolean;
  question: QuizQuestion;
  isLoading?: boolean;
  onSubmit: (data: QuizFormFields) => Promise<QuizQuestion>;
};

export default function QuizForm({
  isActive = true,
  question,
  isLoading,
  onSubmit,
}: QuizFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.quizForm',
  });

  const { identifier } = question;

  const [activeLanguage, setActiveLanguage] =
    useState<SupportedLanguage>(DEFAULT_LANGUAGE);

  const gradeCtx = useQuestionExamGradeCtx();
  const maxGradeDefault = gradeCtx?.getGradeForQuestion(question)?.maxGrade;

  const defaultValues = useMemo<QuizFormFields>(
    () => ({
      title: normalizeTranslatableField(question?.title),
      context: normalizeTranslatableField(question?.context),
      quizAnswers: adapterQuizAnswers(question?.quizAnswers ?? []),
      feedbacks: adapterFeedbacks(question?.feedbacks ?? []),
      maxGrade: maxGradeDefault,
    }),
    [question, maxGradeDefault],
  );

  const { submitForm } = useQuestionFormsOrchestrator();

  const methods = useForm<QuizFormFields>({ defaultValues });
  const { watch, trigger, reset, formState } = methods;

  const { hasChanges, handleReset } = useFormRegistration({
    identifier,
    submit: onSubmit,
    validate: trigger,
    watch,
    reset,
    defaultValues,
    formState,
  });

  const realTimeValues = watch();
  const previewQuestion = useQuizPreviewQuestion(realTimeValues);

  return (
    <div className="flex gap-8">
      <QuestionFormContainer
        methods={methods}
        activeLanguage={activeLanguage}
        onLanguageChange={setActiveLanguage}
        onSubmit={e => {
          e.preventDefault();
          submitForm(identifier);
        }}
        isLoading={isLoading}
        hasChanges={hasChanges}
        onCancel={handleReset}
      >
        <div className="flex flex-col gap-4">
          <QuestionTitleField
            activeLanguage={activeLanguage}
            placeholder={t('titlePlaceholder')}
          />

          <QuestionContextField
            activeLanguage={activeLanguage}
            isActive={isActive}
          />
          <Divider />
          <QuizAnswersForm />
        </div>

        <PointsSection />
      </QuestionFormContainer>

      <div className="flex-1">
        <QuestionPreview question={previewQuestion} />
      </div>
    </div>
  );
}
